import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, DollarSignIcon, TrendingUpIcon, DownloadIcon, FileSpreadsheetIcon, ArchiveIcon, PowerIcon, RotateCcwIcon, BarChartIcon, LockIcon, UserIcon, TagIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Opportunity, OpportunityStage } from '../../types';
import { formatCurrency, getStageName, getStageColor } from '../../utils/helpers';
import { exportOpportunitiesToExcel } from '../../utils/excelExport';
import { exportOpportunitiesToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Opportunities() {
  const {
    opportunities,
    addOpportunity,
    updateOpportunity,
    deleteOpportunity,
    contacts,
    patients
  } = useData();
  const {
    user,
    users
  } = useAuth();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('opportunities');
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  // View Mode State
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  // Confirmation Modals
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactId: '',
    responsibleUserId: '',
    opportunityType: '',
    stage: 'nuevo_lead' as OpportunityStage,
    amount: 0,
    probability: 0,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Load statuses
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-opportunities-status');
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch (e) {
      console.error('Error loading opportunity statuses', e);
    }
  }, []);
  const updateItemStatus = (id: string, status: 'archived' | 'deactivated' | null) => {
    const newStatuses = {
      ...itemStatuses
    };
    if (status === null) {
      delete newStatuses[id];
    } else {
      newStatuses[id] = status;
    }
    setItemStatuses(newStatuses);
    localStorage.setItem('savidna-opportunities-status', JSON.stringify(newStatuses));
    const supabaseStatus = status === null ? 'active' : status;
    updateOpportunity(id, {
      status: supabaseStatus
    } as any).catch(e => console.error('Error updating opportunity status in Supabase:', e));
  };
  const filteredOpportunities = opportunities.filter(o => {
    const status = itemStatuses[o.id];
    const matchesStatus = viewMode === 'active' ? !status : status === viewMode;
    if (!matchesStatus) return false;
    const contactName = o.contactId ? contacts.find(c => c.id === o.contactId)?.name || '' : '';
    const matchesSearch = o.name.toLowerCase().includes(searchTerm.toLowerCase()) || contactName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = !filterStage || o.stage === filterStage;
    return matchesSearch && matchesStage;
  });
  const {
    paginatedItems: paginatedOpportunities,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredOpportunities);
  const stageOptions = [{
    value: 'nuevo_lead',
    label: 'Nuevo Lead'
  }, {
    value: 'contactado',
    label: 'Contactado'
  }, {
    value: 'evaluacion_consulta',
    label: 'Evaluación / Consulta'
  }, {
    value: 'cotizacion_enviada',
    label: 'Cotización Enviada'
  }, {
    value: 'confirmado',
    label: 'Confirmado'
  }, {
    value: 'cita_agendada',
    label: 'Cita Agendada'
  }, {
    value: 'servicio_realizado',
    label: 'Servicio Realizado'
  }];
  const opportunityTypeOptions = [{
    value: 'masaje',
    label: 'Masaje'
  }, {
    value: 'facial',
    label: 'Facial'
  }, {
    value: 'sueroterapia',
    label: 'Sueroterapía'
  }, {
    value: 'ozonoterapia',
    label: 'Ozonoterapía'
  }, {
    value: 'seguimiento_post_servicio',
    label: 'Seguimiento Post-Servicio'
  }, {
    value: 'promocion',
    label: 'Promoción'
  }];
  const contactOptions = [{
    value: '',
    label: 'Seleccionar contacto...'
  }, ...contacts.map(c => ({
    value: c.id,
    label: c.name
  }))];
  const userOptions = [{
    value: '',
    label: 'Seleccionar responsable...'
  }, ...(users.length > 0 ? users.map(u => ({
    value: u.id,
    label: u.name
  })) : user ? [{
    value: user.id,
    label: user.name
  }] : [])];
  const contactSourceLabels: Record<string, string> = {
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    pagina_web: 'Página Web',
    google: 'Google',
    referido: 'Referido',
    otro: 'Otro',
    // Legacy values
    web: 'Web',
    telefono: 'Teléfono',
    redes_sociales: 'Redes Sociales',
    evento: 'Evento'
  };
  const getContactSourceLabel = (contactId: string | null) => {
    if (!contactId) return 'Sin contacto';
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return 'Sin contacto';
    const source = (contact as any).contactSource || 'otro';
    return contactSourceLabels[source] || source;
  };
  const getLeadSourceLabel = (source: string) => {
    return contactSourceLabels[source] || source;
  };
  const getOpportunityTypeLabel = (type: string) => {
    return opportunityTypeOptions.find(o => o.value === type)?.label || type;
  };
  const getResponsibleName = (userId: string | null) => {
    if (!userId) return '-';
    const found = users.find(u => u.id === userId);
    if (found) return found.name;
    if (user && user.id === userId) return user.name;
    return '-';
  };
  // Auto-fill from contact when selected
  const handleContactChange = (contactId: string) => {
    setFormData({
      ...formData,
      contactId
    });
  };
  const openNewForm = () => {
    setSelectedOpportunity(null);
    setFormData({
      name: '',
      contactId: '',
      responsibleUserId: user?.id || '',
      opportunityType: 'masaje',
      stage: 'nuevo_lead',
      amount: 0,
      probability: 0,
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setFormData({
      name: opportunity.name,
      contactId: opportunity.contactId || '',
      responsibleUserId: (opportunity as any).responsibleUserId || '',
      opportunityType: (opportunity as any).opportunityType || 'masaje',
      stage: opportunity.stage,
      amount: opportunity.amount,
      probability: opportunity.probability,
      notes: opportunity.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  // Action Handlers
  const handleArchive = () => {
    if (selectedOpportunity) {
      updateItemStatus(selectedOpportunity.id, 'archived');
      setIsArchiveConfirmOpen(false);
      setSelectedOpportunity(null);
    }
  };
  const handleDeactivate = () => {
    if (selectedOpportunity) {
      updateItemStatus(selectedOpportunity.id, 'deactivated');
      setIsDeactivateConfirmOpen(false);
      setSelectedOpportunity(null);
    }
  };
  const handleRestore = (opportunity: Opportunity) => {
    updateItemStatus(opportunity.id, null);
  };
  const handleDelete = () => {
    if (selectedOpportunity) {
      deleteOpportunity(selectedOpportunity.id);
      const newStatuses = {
        ...itemStatuses
      };
      delete newStatuses[selectedOpportunity.id];
      setItemStatuses(newStatuses);
      localStorage.setItem('savidna-opportunities-status', JSON.stringify(newStatuses));
      setIsDeleteConfirmOpen(false);
      setSelectedOpportunity(null);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (formData.amount < 0) errors.amount = 'El monto no puede ser negativo';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    // Get contactSource from the selected contact
    const selectedContact = formData.contactId ? contacts.find(c => c.id === formData.contactId) : null;
    const contactSource = selectedContact ? selectedContact.contactSource || 'otro' : '';
    const opportunityData = {
      ...formData,
      contactId: formData.contactId || null,
      contactSource,
      responsibleUserId: formData.responsibleUserId || null
    };
    if (selectedOpportunity) {
      updateOpportunity(selectedOpportunity.id, opportunityData);
    } else {
      addOpportunity(opportunityData);
    }
    setIsFormOpen(false);
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = opportunities;
    if (dateFrom && dateTo) {
      dataToExport = opportunities.filter(o => {
        const dateStr = new Date(o.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportOpportunitiesToExcel(dataToExport);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = opportunities;
    if (dateFrom && dateTo) {
      dataToExport = opportunities.filter(o => {
        const dateStr = new Date(o.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportOpportunitiesToPdf(dataToExport);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-2947">
      {/* Header with Gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 p-6 text-white shadow-lg" data-id="element-2948">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-2949">
          <div data-id="element-2950">
            <div className="flex items-center gap-2 mb-1" data-id="element-2951">
              <TrendingUpIcon className="w-6 h-6 text-emerald-100" data-id="element-2952" />
              <h1 className="text-2xl font-bold" data-id="element-2953">Oportunidades</h1>
            </div>
            <p className="text-emerald-100" data-id="element-2954">
              Pipeline de ventas y seguimiento de negocios
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-2955">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-2956">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2958" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2957">
              
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2960" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2959">
              
                Exportar Excel
              </Button>}
            {canCreate('opportunities') && <Button variant="outline" onClick={openNewForm} className="bg-white text-emerald-700 hover:bg-emerald-50 border-transparent shadow-lg shadow-emerald-900/20" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2962" />} data-id="element-2961">
              
                Nueva Oportunidad
              </Button>}
          </div>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2963" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-teal-400/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" data-id="element-2964" />
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-2965">
          <LockIcon className="w-4 h-4" data-id="element-2966" />
          <span data-id="element-2967">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            oportunidades.
          </span>
        </div>}

      {/* View Tabs & Filters */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center" data-id="element-2968">
        <div className="flex p-1 bg-gray-100 dark:bg-gray-800 rounded-xl" data-id="element-2969">
          <button onClick={() => setViewMode('active')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'active' ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2970">
            
            Activas
          </button>
          <button onClick={() => setViewMode('archived')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'archived' ? 'bg-white dark:bg-gray-700 text-amber-600 dark:text-amber-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2971">
            
            Archivadas
          </button>
          <button onClick={() => setViewMode('deactivated')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'deactivated' ? 'bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2972">
            
            Desactivadas
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto" data-id="element-2973">
          <div className="w-full sm:w-48" data-id="element-2974">
            <Select options={[{
            value: '',
            label: 'Todas las etapas'
          }, ...stageOptions]} value={filterStage} onChange={setFilterStage} placeholder="Filtrar por etapa" data-id="element-2975" />
            
          </div>
          <div className="relative flex-1 md:w-64" data-id="element-2976">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2977" />
            <Input placeholder="Buscar oportunidades..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-2978" />
            
          </div>
        </div>
      </div>

      {/* Opportunities Table */}
      <Card padding="none" className="overflow-hidden" data-id="element-2979">
        {/* ── MOBILE CARDS ── */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2980">
          {paginatedOpportunities.length === 0 ? <div className="py-10 text-center text-gray-500 dark:text-gray-400" data-id="element-2981"><p className="text-sm" data-id="element-2982">No se encontraron oportunidades</p></div> : paginatedOpportunities.map(opp => {
          const contactName = opp.contactId ? contacts.find(c => c.id === opp.contactId)?.name || 'Sin asignar' : 'Sin contacto';
          return <div key={opp.id} className="px-4 py-3 flex items-start gap-3" data-id="element-2983">
                <div className="flex-1 min-w-0" data-id="element-2984">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-2985">{opp.name || opp.title || 'Oportunidad'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-2986">{contactName}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-id="element-2987">
                    {opp.stage && <Badge variant="info" className="text-xs py-0 px-1.5" data-id="element-2988">{opp.stage}</Badge>}
                    {opp.amount && <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-id="element-2989">{typeof opp.amount === 'number' ? `$${opp.amount.toLocaleString()}` : opp.amount}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0" data-id="element-2990">
                  {viewMode === 'active' && canEdit('opportunities') && <button onClick={() => openEditForm(opp)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-2991"><EditIcon className="w-4 h-4" data-id="element-2992" /></button>}
                  {viewMode === 'active' && canDelete('opportunities') && <button onClick={() => openActionConfirm(opp, 'archive')} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" data-id="element-2993"><ArchiveIcon className="w-4 h-4" data-id="element-2994" /></button>}
                  {viewMode !== 'active' && <button onClick={() => restoreItem(opp.id)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" data-id="element-2995"><RotateCcwIcon className="w-4 h-4" data-id="element-2996" /></button>}
                </div>
              </div>;
        })}
        </div>

        {/* ── DESKTOP TABLE ── */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-2997">
          <table className="w-full" data-id="element-2998">
            <thead data-id="element-2999">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-3000">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3001">
                  Oportunidad
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3002">
                  Contacto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3003">
                  Origen
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3004">
                  Tipo
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3005">
                  Etapa
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3006">
                  Monto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3007">
                  Probabilidad
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3008">
                  Responsable
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3009">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3010">
              {paginatedOpportunities.length === 0 ? <tr data-id="element-3011">
                  <td colSpan={9} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-3012">
                  
                    No se encontraron oportunidades{' '}
                    {viewMode === 'active' ? '' : viewMode === 'archived' ? 'archivadas' : 'desactivadas'}
                  </td>
                </tr> : paginatedOpportunities.map(opp => {
              const contactName = opp.contactId ? contacts.find(c => c.id === opp.contactId)?.name || 'Sin asignar' : 'Sin asignar';
              const oppType = (opp as any).opportunityType || '';
              const oppResponsible = (opp as any).responsibleUserId || null;
              // Use saved contactSource, fallback to live contact data
              const sourceValue = opp.contactSource || (opp.contactId ? contacts.find(c => c.id === opp.contactId)?.contactSource || '' : '');
              const contactSourceDisplay = sourceValue ? contactSourceLabels[sourceValue] || sourceValue : 'Sin origen';
              return <tr key={opp.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-3013">
                    
                      <td className="py-4 px-6" data-id="element-3014">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-3015">
                          {opp.name}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-3016">
                        <p className="text-sm text-gray-900 dark:text-gray-100" data-id="element-3017">
                          {contactName}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-3018">
                        <Badge variant="info" data-id="element-3019">{contactSourceDisplay}</Badge>
                      </td>
                      <td className="py-4 px-6" data-id="element-3020">
                        {oppType ? <Badge variant="default" data-id="element-3021">
                            {getOpportunityTypeLabel(oppType)}
                          </Badge> : <span className="text-xs text-gray-400 italic" data-id="element-3022">
                            Sin tipo
                          </span>}
                      </td>
                      <td className="py-4 px-6" data-id="element-3023">
                        <div className="flex items-center gap-2" data-id="element-3024">
                          <div className={`w-2.5 h-2.5 rounded-full ${getStageColor(opp.stage)}`} data-id="element-3025" />
                        
                          <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-3026">
                            {getStageName(opp.stage)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900 dark:text-gray-100" data-id="element-3027">
                        {formatCurrency(opp.amount)}
                      </td>
                      <td className="py-4 px-6" data-id="element-3028">
                        <div className="flex items-center gap-3" data-id="element-3029">
                          <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-3030">
                            <div className="h-full bg-emerald-500 rounded-full" style={{
                        width: `${opp.probability}%`
                      }} data-id="element-3031" />
                          
                          </div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400" data-id="element-3032">
                            {opp.probability}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-3033">
                        <div className="flex items-center gap-2" data-id="element-3034">
                          <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center" data-id="element-3035">
                            <UserIcon className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" data-id="element-3036" />
                          </div>
                          <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-3037">
                            {getResponsibleName(oppResponsible)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-3038">
                        <div className="flex items-center justify-end gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-3039">
                          {viewMode === 'active' ? <>
                              {canEdit('opportunities') && <button onClick={() => openEditForm(opp)} className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Editar" data-id="element-3040">
                            
                                  <EditIcon className="w-4 h-4" data-id="element-3041" />
                                </button>}
                              {canDelete('opportunities') && <>
                                  <button onClick={() => {
                          setSelectedOpportunity(opp);
                          setIsArchiveConfirmOpen(true);
                        }} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-3042">
                              
                                    <ArchiveIcon className="w-4 h-4" data-id="element-3043" />
                                  </button>
                                  <button onClick={() => {
                          setSelectedOpportunity(opp);
                          setIsDeactivateConfirmOpen(true);
                        }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-3044">
                              
                                    <PowerIcon className="w-4 h-4" data-id="element-3045" />
                                  </button>
                                </>}
                            </> : canDelete('opportunities') && <button onClick={() => handleRestore(opp)} className="p-2 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-3046">
                          
                                <RotateCcwIcon className="w-4 h-4" data-id="element-3047" />
                              </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="px-6 pb-4" data-id="element-3048">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-3049" />
          
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedOpportunity ? 'Editar Oportunidad' : 'Nueva Oportunidad'} size="lg" icon={<TrendingUpIcon className="w-5 h-5" data-id="element-3051" />} accentColor="emerald" data-id="element-3050">
        
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3052">
          {/* Basic Info */}
          <div data-id="element-3053">
            <div className="flex items-center gap-2 mb-4" data-id="element-3054">
              <span className="w-1.5 h-5 rounded-full bg-emerald-500" data-id="element-3055"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3056">
                Información General
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3057">
              <Input label="Nombre de la Oportunidad" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Paquete de tratamientos faciales" data-id="element-3058" />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3059">
                <Select label="Contacto" options={contactOptions} value={formData.contactId} onChange={handleContactChange} searchable data-id="element-3060" />
                
                <Select label="Usuario Responsable" options={userOptions} value={formData.responsibleUserId} onChange={value => setFormData({
                ...formData,
                responsibleUserId: value
              })} data-id="element-3061" />
                
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3062">
                <div data-id="element-3063">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-3064">
                    Origen del Contacto
                  </label>
                  <div className="px-3 py-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 cursor-not-allowed" data-id="element-3065">
                    {formData.contactId ? getContactSourceLabel(formData.contactId) : 'Selecciona un contacto primero'}
                  </div>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-3066">
                    Se obtiene automáticamente del contacto
                  </p>
                </div>
                <Select label="Tipo de Oportunidad" options={opportunityTypeOptions} value={formData.opportunityType} onChange={value => setFormData({
                ...formData,
                opportunityType: value
              })} data-id="element-3067" />
                
              </div>
            </div>
          </div>

          {/* Deal Info */}
          <div data-id="element-3068">
            <div className="flex items-center gap-2 mb-4" data-id="element-3069">
              <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-3070"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3071">
                Datos del Negocio
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3072">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3073">
                <Select label="Etapa" options={stageOptions} value={formData.stage} onChange={value => setFormData({
                ...formData,
                stage: value as OpportunityStage
              })} data-id="element-3074" />
                
                <Input type="number" label="Monto Estimado (DOP)" value={formData.amount} onChange={e => setFormData({
                ...formData,
                amount: parseFloat(e.target.value) || 0
              })} error={formErrors.amount} min={0} data-id="element-3075" />
                
              </div>
              <div data-id="element-3076">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3077">
                  Probabilidad de Cierre
                </label>
                <div className="flex items-center gap-4" data-id="element-3078">
                  <input type="range" min="0" max="100" step="5" value={formData.probability} onChange={e => setFormData({
                  ...formData,
                  probability: parseInt(e.target.value)
                })} className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-emerald-500" data-id="element-3079" />
                  
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1 rounded-lg min-w-[60px] text-center" data-id="element-3080">
                    {formData.probability}%
                  </span>
                </div>
              </div>
              <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} rows={3} placeholder="Notas adicionales sobre la oportunidad..." data-id="element-3081" />
              
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-3082">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-3083">
              
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3084">
              {selectedOpportunity ? 'Guardar Cambios' : 'Crear Oportunidad'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Archive Confirmation */}
      <Modal isOpen={isArchiveConfirmOpen} onClose={() => setIsArchiveConfirmOpen(false)} title="Archivar Oportunidad" size="sm" data-id="element-3085">
        
        <div className="space-y-4" data-id="element-3086">
          <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg flex items-start gap-3" data-id="element-3087">
            <ArchiveIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" data-id="element-3088" />
            <div data-id="element-3089">
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-id="element-3090">
                ¿Archivar oportunidad?
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1" data-id="element-3091">
                La oportunidad "{selectedOpportunity?.name}" se moverá a la
                lista de archivadas.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3" data-id="element-3092">
            <Button variant="outline" onClick={() => setIsArchiveConfirmOpen(false)} data-id="element-3093">
              
              Cancelar
            </Button>
            <Button onClick={handleArchive} className="bg-amber-500 hover:bg-amber-600 text-white" data-id="element-3094">
              
              Archivar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Deactivate Confirmation */}
      <Modal isOpen={isDeactivateConfirmOpen} onClose={() => setIsDeactivateConfirmOpen(false)} title="Desactivar Oportunidad" size="sm" data-id="element-3095">
        
        <div className="space-y-4" data-id="element-3096">
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg flex items-start gap-3" data-id="element-3097">
            <PowerIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" data-id="element-3098" />
            <div data-id="element-3099">
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-300" data-id="element-3100">
                ¿Desactivar oportunidad?
              </h4>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1" data-id="element-3101">
                La oportunidad "{selectedOpportunity?.name}" se marcará como
                inactiva.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3" data-id="element-3102">
            <Button variant="outline" onClick={() => setIsDeactivateConfirmOpen(false)} data-id="element-3103">
              
              Cancelar
            </Button>
            <Button onClick={handleDeactivate} variant="danger" data-id="element-3104">
              Desactivar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Permanent Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-3105">
        
        <div className="space-y-4" data-id="element-3106">
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-100 dark:border-red-800" data-id="element-3107">
            <p className="text-red-800 dark:text-red-300 font-medium" data-id="element-3108">
              Esta acción es irreversible
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-3109">
              ¿Está seguro de que desea eliminar permanentemente la oportunidad
              "{selectedOpportunity?.name}"?
            </p>
          </div>
          <div className="flex justify-end gap-3" data-id="element-3110">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-3111">
              
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-3112">
              Eliminar Definitivamente
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Oportunidades" isExporting={isExporting} data-id="element-3113" />
      

      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Oportunidades a PDF" isExporting={isPdfExporting} data-id="element-3114" />
      
    </div>;
}